import { Injectable } from '@angular/core';

/**
 * Where the PDF bytes come from: either a plain URL or a (possibly still
 * pending) Blob, e.g. the response of an authenticated download.
 */
export type PdfSource = { url: string } | { blob: Blob | Promise<Blob> };

export interface PdfViewerOptions {
  /** Text shown in the overlay header. */
  title: string;
  source: PdfSource;
  /** Already translated labels; the service does no i18n on its own. */
  loadingLabel?: string;
  errorLabel?: string;
  /** When set, a download button is shown once the bytes are available. */
  download?: { label: string; filename: string };
  /** Called when the document cannot be fetched or parsed. */
  onError?: (error: unknown) => void;
}

export interface PdfViewerHandle {
  close: () => void;
}

/**
 * Full-screen PDF viewer that renders the document client-side with pdf.js,
 * one <canvas> per page.
 *
 * Rendering the pages ourselves is what makes the viewer work everywhere:
 * embedding a PDF in an <iframe> (blob: URL included) is not supported by
 * iPadOS/iOS Safari nor by Chrome on Android, which leave the frame blank or
 * turn the view into a download.
 */
@Injectable({ providedIn: 'root' })
export class PdfViewerService {
  private renderToken = 0;
  private activeDoc?: { destroy: () => void };

  open(options: PdfViewerOptions): PdfViewerHandle {
    const overlay = document.createElement('div');
    overlay.className = 'pdf-overlay';

    const header = document.createElement('div');
    header.className = 'pdf-overlay-header';

    const closeButton = document.createElement('button');
    closeButton.className = 'pdf-overlay-close pb-1';
    closeButton.innerHTML = '&times;';
    closeButton.setAttribute('aria-label', 'Close');

    const title = document.createElement('h5');
    title.className = 'pdf-overlay-title';
    title.textContent = options.title;

    header.appendChild(closeButton);
    header.appendChild(title);

    let downloadButton: HTMLButtonElement | undefined;
    if (options.download) {
      downloadButton = document.createElement('button');
      downloadButton.className = 'pdf-overlay-download';
      downloadButton.innerHTML =
        '<i class="ti ti-cloud-download"></i><span>' + options.download.label + '</span>';
      downloadButton.disabled = true;
      header.appendChild(downloadButton);
    }

    const container = document.createElement('div');
    container.className = 'pdf-overlay-container';

    const loader = document.createElement('div');
    loader.className = 'pdf-overlay-loader';
    loader.innerHTML = `
      <div class="d-flex flex-column align-items-center gap-2">
        <div class="spinner-border text-primary" role="status" style="width:3rem;height:3rem;">
          <span class="visually-hidden">Loading...</span>
        </div>
        ${options.loadingLabel ? `<span style="color:#6b7280;font-size:14px;">${options.loadingLabel}</span>` : ''}
      </div>`;
    container.appendChild(loader);

    const pagesWrap = document.createElement('div');
    pagesWrap.className = 'pdf-overlay-pages';
    container.appendChild(pagesWrap);

    // A new overlay invalidates any render still in flight.
    const renderToken = ++this.renderToken;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };

    const close = () => {
      this.renderToken++;
      this.activeDoc?.destroy();
      this.activeDoc = undefined;
      document.removeEventListener('keydown', handleEscape);
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
        document.body.style.overflow = 'auto';
      }
    };

    closeButton.onclick = close;

    overlay.appendChild(header);
    overlay.appendChild(container);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    document.addEventListener('keydown', handleEscape);

    overlay.onclick = (e) => {
      if (e.target === overlay) {
        close();
      }
    };

    void this.render(options, pagesWrap, loader, downloadButton, renderToken);

    return { close };
  }

  private async render(
    options: PdfViewerOptions,
    pagesWrap: HTMLElement,
    loader: HTMLElement,
    downloadButton: HTMLButtonElement | undefined,
    renderToken: number,
  ) {
    const isStale = () => renderToken !== this.renderToken || !pagesWrap.isConnected;
    try {
      // Use the "legacy" build: it polyfills Promise.withResolvers / structuredClone,
      // which the default build assumes natively (iOS Safari >= 17.4, Chrome >= 119).
      // Older mobile browsers are exactly the target of this viewer, so we cannot
      // rely on those. The legacy build ships no type declarations, hence the cast.
      // @ts-ignore - no types for the legacy subpath; the public API is identical.
      const pdfjs = (await import('pdfjs-dist/legacy/build/pdf.min.mjs')) as typeof import('pdfjs-dist');
      // Must be an absolute URL: pdf.js resolves workerSrc as a module specifier
      // (both for `new Worker(..., {type:'module'})` and the fake-worker dynamic
      // import fallback), and a bare relative path fails with
      // "Failed to resolve module specifier". document.baseURI honours <base href>.
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'assets/pdfjs/pdf.worker.min.mjs',
        document.baseURI,
      ).toString();

      let blob: Blob | undefined;
      if ('blob' in options.source) {
        blob = await options.source.blob;
      } else if (options.download) {
        // Fetch the bytes once and feed both the rendering and the download
        // button, instead of letting pdf.js download the file a second time.
        const response = await fetch(options.source.url);
        if (!response.ok) {
          throw new Error(`Unable to fetch the PDF (HTTP ${response.status})`);
        }
        blob = await response.blob();
      }
      if (isStale()) {
        return;
      }

      if (blob && downloadButton && options.download) {
        const { filename } = options.download;
        downloadButton.disabled = false;
        downloadButton.onclick = () => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        };
      }

      // The backend serves the whole file in one response and does not support
      // HTTP range requests, so disable range/stream to keep this a single
      // simple GET (also avoids a CORS preflight on the Range header).
      const loadingTask = pdfjs.getDocument(
        blob
          ? { data: new Uint8Array(await blob.arrayBuffer()) }
          : {
              url: (options.source as { url: string }).url,
              disableRange: true,
              disableStream: true,
              disableAutoFetch: true,
            },
      );
      const pdf = await loadingTask.promise;

      if (isStale()) {
        pdf.destroy();
        return;
      }
      this.activeDoc = pdf;

      const outputScale = window.devicePixelRatio || 1;
      // Fit each page to the available width (minus horizontal padding).
      const availableWidth = Math.max((pagesWrap.clientWidth || 800) - 24, 200);

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        if (isStale()) {
          pdf.destroy();
          return;
        }

        const baseViewport = page.getViewport({ scale: 1 });
        const scale = availableWidth / baseViewport.width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-overlay-page';
        const context = canvas.getContext('2d');
        if (!context) {
          continue;
        }
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        await page.render({
          canvasContext: context,
          viewport,
          transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
        }).promise;

        if (isStale()) {
          pdf.destroy();
          return;
        }

        pagesWrap.appendChild(canvas);
        if (pageNum === 1) {
          loader.style.display = 'none';
        }
      }

      // Empty document safety net.
      loader.style.display = 'none';
    } catch (error) {
      console.error('Error rendering PDF:', error);
      if (renderToken === this.renderToken && options.errorLabel) {
        loader.innerHTML =
          `<div style="padding:24px;text-align:center;color:#b91c1c;font-size:14px;">` +
          `${options.errorLabel}</div>`;
      }
      options.onError?.(error);
    }
  }
}
